<template>
  <div class="p-6">
    <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-6">
      {{ location ? 'Edit Location' : 'New Location' }}
    </h2>

    <form @submit.prevent="handleSubmit" class="space-y-6">
      <div class="rounded-md border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20 px-4 py-3">
        <p class="text-sm text-primary-900 dark:text-primary-100">
          Locations can be physical offices, service-area-only markets, or hybrid locations. Missing address, phone, website, or Place ID details can limit NAP, GBP, and local SEO checks.
        </p>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
        <UiFormInput v-model="form.name" type="text" />
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <UiControl
          id="location-type"
          label="Location Type"
          hint="Use Service Area Only when the client has city/state coverage but no public office address.">
          <UiSelect
            id="location-type-select"
            v-model="form.location_type"
            :options="locationTypeOptions"
            size="sm" />
        </UiControl>

        <UiControl
          id="location-status"
          label="Location Status"
          hint="Closed, temporarily closed, or unconfirmed locations are excluded from analysis readiness and onboarding checks.">
          <UiSelect
            id="location-status-select"
            v-model="form.status"
            :options="locationStatusOptions"
            size="sm" />
        </UiControl>

        <UiControl
          id="location-primary"
          label="Main Location / Head Office"
          :hint="primaryLocationHint">
          <UiCheckbox
            id="location-primary-checkbox"
            v-model="form.is_primary"
            :disabled="form.status !== 'active'"
            label="Mark as main location" />
        </UiControl>
      </div>

      <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 pt-2">
        Address
      </h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 -mt-4">
        {{ addressRequirementText }}
      </p>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="md:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Address Line 1 <span v-if="requiresFullAddress" class="text-red-400">*</span>
          </label>
          <UiFormInput v-model="form.address_line_1" type="text" :required="requiresFullAddress" />
        </div>
        <div class="md:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Address Line 2</label>
          <UiFormInput v-model="form.address_line_2" type="text" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            City <span class="text-red-400">*</span>
          </label>
          <UiFormInput v-model="form.city" type="text" required />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            State <span class="text-red-400">*</span>
          </label>
          <UiFormInput v-model="form.state" type="text" required />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            ZIP <span v-if="requiresFullAddress" class="text-red-400">*</span>
          </label>
          <UiFormInput v-model="form.zip" type="text" :required="requiresFullAddress" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Country <span v-if="requiresFullAddress" class="text-red-400">*</span>
          </label>
          <UiFormInput v-model="form.country" type="text" :required="requiresFullAddress" />
        </div>
      </div>

      <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 pt-2">
        Contact
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
          <UiFormInput v-model="form.phone_number" type="phone" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
          <UiFormInput v-model="form.email" type="email" />
        </div>
        <div class="md:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Website URL</label>
          <UiFormInput v-model="form.website_url" type="text" />
        </div>
      </div>

      <UiControl
        id="location-services"
        label="Location Services"
        hint="Leave empty to use the business list. Add entries only when this location offers a different set of services.">
        <ServiceListEditor v-model="form.services" :inherited-services="businessServices" />
      </UiControl>

      <div>
        <div class="flex items-center justify-between gap-3">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Place ID</label>
          <a
            v-if="googlePlaceSearchUrl"
            :href="googlePlaceSearchUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="mb-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline">
            Find on Google
          </a>
        </div>
        <UiFormInput v-model="form.place_id" type="text" />
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Place ID improves GBP matching, competitor scans, review pulls, and local NAP verification. Use Find on Google to search by the current name and address, then paste the confirmed Place ID here.
        </p>
      </div>

      <div v-if="errors" class="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
        <p class="text-sm text-red-700 dark:text-red-400">
          {{ errors }}
        </p>
      </div>

      <div class="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          @click="$emit('on-close-panel')"
          class="px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
          Cancel
        </button>
        <button
          type="submit"
          :disabled="saving"
          class="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50">
          {{ saving ? 'Saving...' : (location ? 'Update' : 'Create') }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { createLocation, updateLocation } from '~/javascript/api/locations.js'
import { getBusiness } from '~/javascript/api/businesses.js'
import { UiFormInput } from '~/javascript/components/ui/form/input'
import UiCheckbox from '~/javascript/components/ui/form/checkbox/checkbox.vue'
import UiControl from '~/javascript/components/ui/form/control/control.vue'
import UiSelect from '~/javascript/components/ui/form/select/select.vue'
import ServiceListEditor from '~/javascript/components/ui/form/service_list_editor.vue'

const props = defineProps({
  location: { type: Object, default: null },
  businessId: { type: String, required: true },
  prefill: { type: Object, default: null }
})

const emit = defineEmits(['on-close-panel', 'saved'])

const saving = ref(false)
const errors = ref(null)
const businessServices = ref([])

const locationTypeOptions = [
  { id: 'physical_office', title: 'Physical Office' },
  { id: 'service_area_only', title: 'Service Area Only' },
  { id: 'hybrid_location', title: 'Hybrid Location' }
]

const locationStatusOptions = [
  { id: 'active', title: 'Active' },
  { id: 'closed', title: 'Closed' },
  { id: 'temporarily_closed', title: 'Temporarily Closed' },
  { id: 'needs_client_confirmation', title: 'Needs Client Confirmation' }
]

const defaultForm = {
  name: '',
  location_type: 'physical_office',
  status: 'active',
  is_primary: false,
  address_line_1: '',
  address_line_2: '',
  city: '',
  state: '',
  country: '',
  zip: '',
  phone_number: '',
  email: '',
  website_url: '',
  place_id: '',
  services: []
}

const form = ref({ ...defaultForm })

function normalizeServices(services) {
  return (services || []).map((service) => ({
    name: String(service?.name || ''),
    description: String(service?.description || '')
  }))
}

const requiresFullAddress = computed(() => form.value.location_type !== 'service_area_only')

const primaryLocationHint = computed(() => (
  form.value.status === 'active' ?
    'Used as the main reference point for homepage analysis, NAP checks, and primary location recommendations.' :
    'Only active locations can be used as the main location for analysis.'
))

const addressRequirementText = computed(() => (
  requiresFullAddress.value ?
    'Physical and hybrid locations require a full street address, city, state, ZIP, and country.' :
    'Service-area-only entries require city and state; ZIP is optional when the client only lists city/state coverage.'
))

const placeIdSearchQuery = computed(() => [
  form.value.name,
  form.value.address_line_1,
  form.value.city,
  form.value.state,
  form.value.zip
].filter(Boolean).join(' '))

const googlePlaceSearchUrl = computed(() => {
  if (!placeIdSearchQuery.value) return ''

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(placeIdSearchQuery.value)}`
})

watch(() => form.value.status, (status) => {
  if (status !== 'active') form.value.is_primary = false
})

const handleSubmit = async() => {
  try {
    saving.value = true
    errors.value = null

    if (props.location) {
      await updateLocation(props.location.id, { location: form.value })
    } else {
      await createLocation(props.businessId, { location: form.value })
    }
    emit('saved')
  } catch (error) {
    if (error.response?.data?.details) {
      errors.value = Object.values(error.response.data.details).flat().join(', ')
    } else {
      errors.value = 'An error occurred. Please try again.'
    }
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  if (props.location) {
    Object.keys(defaultForm).forEach((key) => {
      if (key === 'is_primary') {
        form.value[key] = Boolean(props.location[key])
      } else if (key === 'services') {
        form.value[key] = normalizeServices(props.location[key])
      } else {
        form.value[key] = props.location[key] || defaultForm[key]
      }
    })
  } else if (props.prefill) {
    // First location for a business: reuse the details already entered on
    // the business so they don't have to be typed twice.
    Object.keys(defaultForm).forEach((key) => {
      if (props.prefill[key]) form.value[key] = props.prefill[key]
    })
  }

  getBusiness(props.businessId).then(({ data }) => {
    businessServices.value = normalizeServices(data.services)
  }).catch(() => {})
})
</script>
